// Copyright (C) 2012-present, The Authors. This program is free software: you can redistribute it and/or  modify it under the terms of the GNU Affero General Public License, version 3, as published by the Free Software Foundation. This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details. You should have received a copy of the GNU Affero General Public License along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { Heading, Link, Text } from 'theme-ui'
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { useAuth } from 'react-oidc-context'
import PolisNet from '../../util/net'
import Url from '../../util/url'
import strings from '../../strings/strings'

const { urlPrefix } = Url

const getCurrentTimestamp = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`
}

const getDownloadFilename = (conversation_id) => {
  return `${getCurrentTimestamp()}-${conversation_id}-xid.csv`
}

const ParticipantXids = ({ conversation_id }) => {
  const { isLoading, isAuthenticated } = useAuth()

  const [state, setState] = useState({
    conversationUuid: null,
    isLoading: true,
    error: null
  })

  const loadConversationUuid = () => {
    // Use PolisNet.polisGet to ensure proper authorization header
    PolisNet.polisGet('/api/v3/conversationUuid', {
      conversation_id: conversation_id
    })
      .then((data) => {
        setState({
          conversationUuid: data.conversation_uuid,
          isLoading: false,
          error: null
        })
      })
      .catch((error) => {
        console.error('Error fetching UUID:', error)
        setState({
          conversationUuid: null,
          error: 'Failed to fetch conversation UUID',
          isLoading: false
        })
      })
  }

  const loadConversationUuidIfNeeded = () => {
    // Only load if we have a conversation ID and Auth is ready (not loading)
    if (conversation_id && !isLoading && !state.conversationUuid) {
      loadConversationUuid()
    }
  }

  useEffect(() => {
    // Try to load conversation UUID when component mounts
    loadConversationUuidIfNeeded()
  }, [])

  useEffect(() => {
    // Try again if conversation_id changes or auth state changes
    loadConversationUuidIfNeeded()
  }, [conversation_id, isLoading, isAuthenticated])

  const { conversationUuid, isLoading: uuidLoading, error } = state

  // Only calculate these values if we have a UUID
  const downloadFilename = getDownloadFilename(conversation_id)

  return (
    <div>
      <Heading
        as="h3"
        sx={{
          fontSize: [3, null, 4],
          lineHeight: 'body',
          mb: [3, null, 4],
          display: 'block'
        }}>
        {strings('dist_download_xid_csv')}
      </Heading>

      {uuidLoading ? (
        // Show loading indicator while fetching UUID
        <Text
          sx={{
            display: 'block',
            mb: [3]
          }}>
          {strings('dist_loading_conversation_uuid')}
        </Text>
      ) : error ? (
        // Show error message if failed to fetch UUID
        <Text
          sx={{
            display: 'block',
            mb: [3],
            color: 'error'
          }}>
          {strings('dist_could_not_load_conversation_uuid')}
        </Text>
      ) : conversationUuid ? (
        // Only show download links if we have the UUID
        <>
          <Text
            sx={{
              display: 'block',
              mb: [2]
            }}>
            <a
              download={downloadFilename}
              href={`${urlPrefix}api/v3/xid/${conversationUuid}-xid.csv`}
              type="text/csv">
              {`${strings('dist_xid_csv_download')}: ${downloadFilename}`}
            </a>
          </Text>

          <Text
            sx={{
              display: 'block',
              mb: [3]
            }}>
            {`curl: ${urlPrefix}api/v3/xid/${conversationUuid}-xid.csv`}
          </Text>
        </>
      ) : (
        // Fallback message when UUID is null but no error occurred
        <Text
          sx={{
            display: 'block',
            mb: [3]
          }}>
          {strings('dist_no_conversation_uuid_available')}
        </Text>
      )}

      <Heading
        as="h3"
        sx={{
          fontSize: [3, null, 4],
          lineHeight: 'body',
          mb: [3, null, 4],
          mt: 4,
          display: 'block'
        }}>
        {strings('dist_what_is_an_xid')}
      </Heading>

      <ul>
        <li>
          {strings('xid_sometimes_the')}
          <Link target="_blank" href="https://compdemocracy.org/owner">
            {strings('xidg_owner_nom')}
          </Link>
          {strings('xid_of_a')}
          <Link target="_blank" href="https://compdemocracy.org/conversation">
            {strings('xidg_conversation_gen')}
          </Link>
          {strings('xid_has_existing_linkage')}
          <Link target="_blank" href="https://compdemocracy.org/participant">
            {strings('xidg_participants_gen')}
          </Link>
          {strings('xid_email_campaign_login_wall')}
        </li>

        <li>
          {strings('xid_a_note_using')}
          <Link target="_blank" href="https://compdemocracy.org/xid">
            xid
          </Link>
          {strings('xid_assumes_that_the')}
          <Link target="_blank" href="https://compdemocracy.org/owners">
            {strings('xidg_owner_nom')}
          </Link>
          {strings('xid_has_the_token_different_from')}
          <Link target="_blank" href="https://compdemocracy.org/creating-single-use-urls">
            {strings('xid_creating_single_use_urls')}
          </Link>
        </li>

        <li>
          <Link target="_blank" href="https://compdemocracy.org/xid">
            xid
          </Link>
          {strings('xid_works_in_embedded_case')}
          <Link target="_blank" href="https://compdemocracy.org/owners">
            {strings('xidg_owner_nom')}
          </Link>
          {strings('xid_has_added_the')}
          <Link target="_blank" href="https://compdemocracy.org/embed-code">
            {strings('xid_embed_code_label')}
          </Link>
          {strings('xid_to_page_on_web_property')}
        </li>

        <li>
          {strings('xid_once_the')}
          <Link target="_blank" href="https://compdemocracy.org/conversation">
            {strings('xidg_conversation_acc')}
          </Link>
          {strings('xid_embedded_third_party_add_attribute')}
          <code>data-xid=&quot;test&quot;</code>
        </li>

        <li>
          {strings('xid_the_value_prefix')}
          <Link target="_blank" href="https://compdemocracy.org/xid">
            xid
          </Link>
          {strings('xid_value_available_on_record')}
          <Link target="_blank" href="https://compdemocracy.org/export">
            {strings('xid_export_label')}
          </Link>
        </li>

        <li>
          <Link target="_blank" href="https://compdemocracy.org/xid">
            {strings('xid_example')}
          </Link>
          <ul>
            <li>
              {strings('xid_common_workflow_for_using')}
              <Link target="_blank" href="https://compdemocracy.org/xid">
                xid
              </Link>
              {strings('xid_involves_table_demographic')}
            </li>

            <li>
              <Link target="_blank" href="https://compdemocracy.org/participant">
                {strings('xidg_participants_nom')}
              </Link>
              {strings('xid_sent_email_invited')}
            </li>

            <li>
              {strings('xid_then_when_the')}
              <Link target="_blank" href="https://compdemocracy.org/participant">
                {strings('xidg_participant_nom')}
              </Link>
              {strings('xid_clicks_through_custom_url')}
              <div sx={{ display: 'inline-block' }}>
                <code>data-xid=&quot;someTokenFromTheURLBarThatIdentifiesTheUser&quot;</code>
              </div>
            </li>
          </ul>
        </li>

        <li>{strings('dist_embed_code_loginless_param')}</li>

        <li>
          {strings('xid_usage')}
          <code>data-xid=&quot;guid&quot;</code>
          {strings('xid_or_comma')}
          <code>data-xid=&quot;5647434556754623&quot;</code>
          {strings('xid_or_less_anonymously')}
          <code>data-xid=&quot;foo@bar.com&quot;</code>
        </li>
      </ul>
    </div>
  )
}

ParticipantXids.propTypes = {
  conversation_id: PropTypes.string.isRequired
}

export default ParticipantXids
